/**
 * Where an in-season KPI card opens a table on its own page or another (ADR 9069). Pure.
 *
 * A plain `DataTable` keeps its column filters in the address — `?<urlKey>.f.<column>=v:<a>,<b>`
 * (`src/planning/data-table.tsx`) — and reads them back on load and on back/forward. A card that
 * counts the rows a column filter keeps links to exactly that address, so the table it lands on is
 * the card's number row for row. The table is given an EXPLICIT `urlKey`: the default is derived
 * from its caption and columns and would break the link the day either is reworded.
 */

import { rankedViewHref, tableFilterHref, type ColumnFilterSpec } from '@/planning';

import { LINES_PARAM, LINES_SERVED } from './drill';
import { PROPOSALS_VIEW_PARAM, type ProposalsView } from './proposals-view';
import { AS_OF_PARAM, RUN_ID_PARAM } from './scope';

export { rankedViewHref };

/** The id of the element that holds the Allocation table (or its store and article views): where the run-summary cards scroll to. */
export const ALLOCATION_TABLE_ID = 'allocation-proposals';

/**
 * The Allocation page narrowed to the lines its run-summary cards count (ADR 9069): the run and the date the card counted
 * under, no ranking, no filter and no open panel of the view before, `?lines=served` (the dropped doors' markers out, the
 * platform's `lines=served`, ADR 6193) and the view the card counts in — the lines (`line`) or the stores (`store`).
 */
export function servedProposalsHref(path: string, search: string, view: ProposalsView): string {
  const base = rankedViewHref(path, search, {}, undefined, undefined, [RUN_ID_PARAM, AS_OF_PARAM]);
  return withServedLines(base, view === 'line' ? {} : { [PROPOSALS_VIEW_PARAM]: view }, ALLOCATION_TABLE_ID);
}

/**
 * `href` (path and query, no fragment) with `?lines=served` and the `extra` parameters set, and `fragment` as the element to
 * scroll to: the narrowing a card adds to a list the platform reads without the dropped doors' markers (ADR 6193). Pure.
 */
export function withServedLines(href: string, extra: Readonly<Record<string, string>>, fragment: string): string {
  return withParams(href, { [LINES_PARAM]: LINES_SERVED, ...extra }, fragment);
}

/** `href` (path and query) with `set` written into its query and `fragment` as the element to scroll to. Pure. */
export function withParams(href: string, set: Readonly<Record<string, string>>, fragment: string): string {
  const target = href.split('#')[0] ?? '';
  const at = target.indexOf('?');
  const params = new URLSearchParams(at < 0 ? '' : target.slice(at + 1));
  for (const [key, value] of Object.entries(set)) params.set(key, value);
  return `${at < 0 ? target : target.slice(0, at)}?${params.toString()}#${fragment}`;
}

/** The id the transfers cards scroll to: the action types' grids, which hold the run's transfers (ADR 9069). */
export const TRANSFER_GRIDS_ID = 'transfer-grids';

/**
 * A card whose table is on the SAME page: the page's own address (path and parameters), the table's filters
 * `filters` and nothing of its previous view, and the table's id as the fragment the card scrolls to. The
 * table's `urlKey` is also the id of the element that wraps it.
 */
export function samePageTableHref(
  search: string,
  urlKey: string,
  filters: Readonly<Record<string, ColumnFilterSpec>> = {},
): string {
  return tableFilterHref(typeof window === 'undefined' ? '' : window.location.pathname, search, urlKey, filters, { fragment: urlKey });
}
